import React, { memo, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';
import useSWR from 'swr';

import { AxiosResponse } from 'axios';
import { OpportunityCardLink } from 'components/opportunity-page';
import { ButtonV3, Chip, FreeText, Stat } from 'components/ComponentV2';
import Icon, { IconName } from 'components/Icon';
import IconButton from 'components/IconButton';
import Avatar from 'components/Avatar';
import Link from 'components/Link';
import { HuzzleUserAvatar } from 'components/HuzzleUserAvatar';
import { UserActionEnum } from 'hooks/useOpportunity/useOpportunityInternal';
import { parseArrayResponse } from 'lib/utils/parser';
import { TStudentSociety } from 'lib/models/student-society';
import { CommitteeAttributes } from 'lib/models/committee';
import { API_ROUTES } from 'lib/api-routes';
import { OpportunitiesResponse } from 'lib/models/opportunity';
import { apiInstance } from 'lib/utils/axios';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { UserAvatarFields } from 'lib/models/User.model';
import { getUserAvatarFields } from 'utils/user';

import styles from './SocietyInfo.module.scss';

type SocietyInfoProps = {
  className?: string;
  society: TStudentSociety;
  showFullContent?: boolean;
};

const ContentSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className={styles['content-section']}>
      <h3 className={styles['title']}>{title}</h3>
      {children}
    </div>
  );
};

const ManagerCard = ({ manager }: { manager: CommitteeAttributes }) => {
  return (
    <div className={styles['team-member']}>
      <Avatar icon={manager.avatar_url} name={manager.name} size="medium" className={styles.avatar} />
      <div className={styles['info']}>
        <h4 className={styles['name']}>{manager.name}</h4>
        <p className={styles['role']}>{manager.role}</p>
      </div>
    </div>
  );
};

const SocietyOpportuntiesList = ({ slug, societyId }: { slug: string; societyId: string }) => {
  const [t] = useTranslation('opportunity');
  const { isSearchPage } = useOpportunity();
  const companyOpportunitiesPathname = `${API_ROUTES.STUDENT_SOCIETIES}/${slug}/touchpoints?touchpointable_type=Event&with_details=true&page=1&per_page=3&sort_by=posted_at`;
  const { data: companypportunitiesResponse } = useSWR<AxiosResponse<OpportunitiesResponse>>(companyOpportunitiesPathname, apiInstance.get, {
    revalidateOnFocus: false,
  });
  const opportunities = parseArrayResponse(companypportunitiesResponse?.data);
  if (opportunities.length === 0) {
    return null;
  }
  const similarOpportunitiesPathname = `${PAGE_ROUTES.EVENTS_SEARCH}&posted_by_organization_ids=${societyId}`;

  return (
    <div className={styles['content-section']}>
      <h3 className={cx(styles.title, styles['events-header'])}>
        {t('society-info.content.events')}
        {!isSearchPage && (
          <Link href={similarOpportunitiesPathname} className={styles.link}>
            <span>{t('society-info.view-all')}</span>
            <Icon iconName="icon_arrow-right-2" size="small" />
          </Link>
        )}
      </h3>
      <ul className={styles['opportunities-list']}>
        {opportunities.map((opportunity) => (
          <OpportunityCardLink key={opportunity.id} opportunity={opportunity} />
        ))}
      </ul>
    </div>
  );
};

export const SocietyInfo: React.FC<SocietyInfoProps> = memo(({ className, society, showFullContent = true }: SocietyInfoProps) => {
  const [t] = useTranslation('opportunity');
  const { updateUserAction, isFollowLoading, queryUserAction, queryUserActionId } = useOpportunity();
  const {
    id: societyId,
    name,
    slug,
    icon_url,
    short_description,
    description,
    website,
    linkedin_url,
    facebook_url,
    instagram_url,
    student_union_url,
    twitter_url,
    topics: topicsResponse,
    managers: managersResponse,
    offerings: offeringsResponse,
    stats: statsResponse,
    student_societies_highlights: studentSocietiesHighlightsResponse,
    subscribers_require_approval,
    awaiting_approval,
    subscribed,
  } = society;

  const topics = parseArrayResponse(topicsResponse);
  const managers = parseArrayResponse(managersResponse);
  const offerings = parseArrayResponse(offeringsResponse);
  const highlights = parseArrayResponse(studentSocietiesHighlightsResponse);
  const awaitingApproval = subscribers_require_approval && awaiting_approval;
  const socialMediaLinks = useMemo(
    () =>
      [
        { icon: 'facebook', url: facebook_url },
        { icon: 'icon_x', url: twitter_url },
        { icon: 'instagram', url: instagram_url },
        { icon: 'su', url: student_union_url },
        { icon: 'linkedin', url: linkedin_url },
      ]
        .filter(({ url }) => url)
        .sort((a, b) => {
          if (a.url && b.url) return 0;
          else if (a.url) return -1;
          else if (b.url) return 1;
          return 0;
        }),
    [facebook_url, twitter_url, instagram_url, student_union_url, linkedin_url]
  );
  const stats = parseArrayResponse(statsResponse);
  const publicURL = PAGE_ROUTES.SOCIETY_PUBLIC.replace('[slug]', slug);

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.header}>
        <div className={styles.top}>
          <HuzzleUserAvatar
            className={styles['img-container']}
            avatarClassName={styles.logo}
            url={publicURL}
            user={getUserAvatarFields({ society }) as UserAvatarFields}
            size="medium"
          />
          <Link href={publicURL} underlineOnHover={false}>
            <h2 className={styles.title}>{name}</h2>
          </Link>
        </div>
        <p className={styles.description}>{short_description}</p>
        {stats.length > 0 && (
          <div className={styles.stats}>
            {stats.map(({ id, title, value }) => (
              <Stat key={id} value={value} label={title} />
            ))}
          </div>
        )}
      </div>
      {showFullContent && (
        <div className={styles.content}>
          <ContentSection title={t('society-info.content.about')}>
            <FreeText content={description} />
            <div className={styles.chips}>
              {topics.map((topic) => (
                <Chip key={topic.id}>{topic.name}</Chip>
              ))}
            </div>
          </ContentSection>
          {managers.length > 0 && (
            <ContentSection title={t('society-info.content.team')}>
              <div className={styles['team-members']}>
                {managers.map((manager) => (
                  <ManagerCard key={manager.id} manager={manager} />
                ))}
              </div>
            </ContentSection>
          )}
          {offerings.length > 0 && (
            <ContentSection title={t('society-info.content.offerings')}>
              <div className={styles.chips}>
                {offerings.map((offering) => (
                  <Chip key={offering.id}>{offering.name}</Chip>
                ))}
              </div>
            </ContentSection>
          )}
          {highlights.length > 0 && (
            <ContentSection title={t('society-info.content.highlights')}>
              <div className={styles.chips}>
                {highlights.map((highlight) => (
                  <Chip key={highlight.id}>{highlight.name}</Chip>
                ))}
              </div>
            </ContentSection>
          )}
          <SocietyOpportuntiesList slug={slug} societyId={societyId} />
        </div>
      )}
      <footer className={styles.footer}>
        {subscribed || awaitingApproval ? (
          <ButtonV3
            color="secondary"
            startIcon={awaitingApproval ? 'icon_check' : 'icon_check-double'}
            isFullWidth
            borderless
            onClick={() => updateUserAction({ userAction: UserActionEnum.DISJOIN_SOCIETY, userActionId: societyId })}
            disabled={isFollowLoading && queryUserAction === UserActionEnum.DISJOIN_SOCIETY && queryUserActionId === societyId}
          >
            {awaitingApproval ? t('society-info.footer.request-sent') : t('society-info.footer.member')}
          </ButtonV3>
        ) : (
          <ButtonV3
            color="secondary"
            isFullWidth
            borderless
            onClick={() => updateUserAction({ userAction: UserActionEnum.JOIN_SOCIETY, userActionId: societyId })}
            disabled={isFollowLoading && queryUserAction === UserActionEnum.JOIN_SOCIETY && queryUserActionId === societyId}
          >
            {t('society-info.footer.join')}
          </ButtonV3>
        )}
        <Link href={publicURL} underlineOnHover={false}>
          <ButtonV3 color="secondary" isFullWidth borderless>
            {t('society-info.footer.view-profile')}
          </ButtonV3>
        </Link>
        <div className={styles['footer-content']}>
          {website && (
            <a href={website} rel="noreferrer" target="_blank" className={styles.website}>
              <ButtonV3 color="secondary" startIcon="icon_launch" className={cx(styles.btn, styles.bgColor)} borderless>
                {t('society-info.footer.website')}
              </ButtonV3>
            </a>
          )}
          {socialMediaLinks.map(({ icon, url }) => (
            <a href={url} key={icon} rel="noreferrer" target="_blank">
              <IconButton iconName={icon as IconName} className={cx(styles.icon, styles.bgColor)} />
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
});

SocietyInfo.displayName = 'SocietyInfo';
